import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Check, Eye, TrendingUp, TrendingDown, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Feedback() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [feedbackData, setFeedbackData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(10);
  const [canComplete, setCanComplete] = useState(false);
  const [reflection, setReflection] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState(false); // For viewing completed feedback
  const timerRef = useRef(null);

  useEffect(() => {
    fetchFeedbackData();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [id]);

  useEffect(() => {
    // Only run timer if not in view mode
    if (feedbackData && !viewMode && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setCanComplete(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [feedbackData, viewMode]);

  const fetchFeedbackData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions/${id}/feedback`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al cargar datos de feedback');
      }
      
      const data = await response.json();
      setFeedbackData(data);
      
      // If feedback already completed, switch to view mode
      if (data.feedback_completed) {
        setViewMode(true);
        setCanComplete(true);
        setReflection(data.feedback_reflection || '');
      }
    } catch (error) {
      toast.error(error.message);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!canComplete) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/sessions/${id}/feedback`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reflection: reflection || null })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al enviar feedback');
      }
      
      toast.success('¡Bucle retrocausal cerrado!', {
        description: 'Puedes continuar con una nueva sesión.'
      });
      navigate('/');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[hsl(198,62%,54%)]"></div>
      </div>
    );
  }

  if (!feedbackData) {
    return null;
  }

  const isHit = feedbackData.result === 'hit';

  return (
    <div className="min-h-screen bg-[hsl(232,36%,7%)] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-4">
            <Eye className="w-4 h-4 text-[hsl(198,62%,54%)]" />
            <span className="text-sm text-white/70">
              {viewMode ? 'Feedback Completado' : 'Cierre del Bucle Retrocausal'}
            </span>
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">
            {feedbackData.session_code}
          </h1>
          
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
            isHit 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : 'bg-red-500/20 text-red-400'
          }`}>
            {isHit ? <Check className="w-5 h-5" /> : null}
            <span className="font-medium">
              {isHit ? '¡ACIERTO!' : 'Error en predicción'}
            </span>
          </div>
        </motion.div>

        {/* Market Result */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6 mb-6 text-center"
        >
          <p className="text-white/60 mb-2">El mercado</p>
          <div className={`inline-flex items-center gap-3 text-2xl font-bold ${
            feedbackData.actual_dir === 'UP' ? 'text-emerald-400' : 'text-red-400'
          }`}>
            {feedbackData.actual_dir === 'UP' 
              ? <TrendingUp className="w-8 h-8" />
              : <TrendingDown className="w-8 h-8" />
            }
            <span>{feedbackData.actual_dir === 'UP' ? 'SUBIÓ' : 'BAJÓ'}</span>
          </div>
          <p className="text-sm text-white/40 mt-2">{feedbackData.symbol}</p>
        </motion.div>

        {/* Winning Image - Full Screen Focus */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-6"
        >
          <p className="text-center text-white/60 mb-4">
            Esta era la imagen correcta. Obsérvala con atención:
          </p>
          
          <div className="relative rounded-xl overflow-hidden border-2 border-[hsl(198,62%,54%)] shadow-lg shadow-[hsl(198,62%,54%)]/20">
            <img
              src={feedbackData.winning_image_url}
              alt="Imagen ganadora"
              className="w-full h-auto max-h-[50vh] object-contain bg-black"
            />
            
            {/* Timer overlay */}
            {!canComplete && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="bg-black/80 px-6 py-4 rounded-xl border border-white/20 text-center">
                  <Clock className="w-8 h-8 text-[hsl(198,62%,54%)] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{timeRemaining}s</p>
                  <p className="text-sm text-white/60">Observa la imagen</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* User's Drawing Comparison */}
        {feedbackData.viewer_sketch_url && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-4 mb-6"
          >
            <p className="text-sm text-white/60 mb-3 text-center">Tu dibujo original:</p>
            <div className="flex justify-center">
              <img
                src={feedbackData.viewer_sketch_url}
                alt="Tu dibujo"
                className="max-h-40 rounded-lg border border-white/10"
              />
            </div>
          </motion.div>
        )}

        {/* Reflection Field */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6 mb-6"
        >
          <label className="block text-white/70 mb-3">
            {viewMode ? 'Tu reflexión:' : '¿Qué similitudes notas entre tu dibujo y la imagen? (opcional)'}
          </label>
          {viewMode ? (
            <p className="text-white/80 bg-[hsl(232,36%,12%)] rounded-lg p-4 min-h-[4rem]">
              {reflection || <span className="text-white/40 italic">Sin reflexión guardada</span>}
            </p>
          ) : (
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="Describe las similitudes que observas..."
              className="w-full bg-[hsl(232,36%,12%)] border border-[hsl(232,18%,18%)] rounded-lg p-4 text-white placeholder-white/30 resize-none h-24 focus:outline-none focus:ring-2 focus:ring-[hsl(198,62%,54%)]/50"
              data-testid="feedback-reflection-input"
            />
          )}
        </motion.div>

        {/* Progress Bar - only when not in view mode */}
        {!viewMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mb-6"
          >
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[hsl(198,62%,54%)] to-[hsl(252,37%,60%)]"
                initial={{ width: '0%' }}
                animate={{ width: `${((10 - timeRemaining) / 10) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </motion.div>
        )}

        {/* Submit/Back Button */}
        {viewMode ? (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => navigate(-1)}
            className="w-full py-4 rounded-xl font-medium text-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            ← Volver
          </motion.button>
        ) : (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            onClick={handleSubmit}
            disabled={!canComplete || submitting}
            data-testid="complete-feedback-button"
            className={`w-full py-4 rounded-xl font-medium text-lg transition-all ${
              canComplete && !submitting
                ? 'bg-gradient-to-r from-[hsl(198,62%,54%)] to-[hsl(252,37%,60%)] text-white hover:opacity-90 cursor-pointer'
                : 'bg-white/10 text-white/30 cursor-not-allowed'
            }`}
          >
            {submitting ? 'Completando...' : canComplete ? 'Completar Feedback' : `Espera ${timeRemaining} segundos...`}
          </motion.button>
        )}

        <p className="text-center text-white/40 text-sm mt-4">
          {viewMode 
            ? `Feedback completado el ${feedbackData.feedback_completed_at ? new Date(feedbackData.feedback_completed_at).toLocaleDateString('es-ES') : ''}`
            : 'Este paso cierra el bucle retrocausal y refuerza tu conexión intuitiva.'
          }
        </p>
      </div>
    </div>
  );
}
